import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Subscription, Observable, of } from "rxjs";
import { map, mergeMap, tap } from "rxjs/operators";
import { DocumentManagementService } from "../../../../services/document-management.service";
import { Organization } from "../../../../models/organization.model";
import { OnBaseQueryModel, OnBaseKeyword, OnBaseKeywordQuery, QueryResult, KeywordRelation } from "../../../../models/onBaseIntegrationAndMapping.model";
import { SortAttribute, SortArgs, SortDirection } from "../../../../../core/models/sort.model";
import { Dialog, DialogButton } from "../../../../../core/models/dialog.model";
import { DynamicDialogService } from "../../../../../core/services/dynamic-dialog.service";
import { PageData } from "../../../../models/paging.model";
import { Option } from "../../../../models/flow.dto";


@Component({
    selector: 'on-base-organization-documents',
    templateUrl: './on-base-organization-documents.component.html',
    styleUrls: ['./on-base-organization-documents.component.scss'],
    standalone: false
})
export class OnBaseOrganizationDocumentsComponent implements OnInit, OnDestroy {


    documents: QueryResult;
    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", false, () => { return; })]);
    private _defaultLoadingMessage: string = "Searching for documents.. please wait";
    private _loadingMessage: string;
    get loadingMessage(): string {
        return this._loadingMessage;
    }
    helpText: string = "The identifier option will determine whether documents will be searched by the RICS Owner #, RBDMS Owner # (if applicable), or both.  Documents received via RBDMS will only have the RBDMS Owner # associated.";

    identifierOptions: Option[] = [];
    @Input() organization: Organization;
    identifier: string = "both";

    query: OnBaseQueryModel;

    rbdmsNumberKeyword: OnBaseKeyword;
    requiredKeywords: OnBaseKeywordQuery[];
    ricsNumberKeyword: OnBaseKeyword;
    ricsOption: Option = { id: "rics", text: "RICS Owner #" };
    rbdmsOption: Option = { id: "rbdms", text: "RBDMS Owner #" };
    bothOption: Option = { id: "both", text: "Both RICS & RBDMS Owner #'s" };

    showLoader: boolean = false;
    subscription: Subscription = new Subscription();

    constructor(private readonly _docService: DocumentManagementService, public readonly dialogService: DynamicDialogService) { }

    ngOnInit() {
        this.query = {
            documentTypeIds: [],
            documentTypeNames: [],
            matchAll: true,
            maxResults: 50,
            resultsPerPage: 25,
            pageNumber: 1,
            displayKeywordTypes: [],
            searchParameters: [],
            sort: SortAttribute.DocumentID,
            isAscending: true
        } as OnBaseQueryModel;
        const word$ = this.getRequiredKeywords().pipe(
            tap((words: OnBaseKeyword[]) => {
                this.requiredKeywords = [];
                let requiredRics = new OnBaseKeywordQuery(this.ricsNumberKeyword.id, this.ricsNumberKeyword.name, this.organization.id, "eq");
                requiredRics.relation = KeywordRelation.And;
                this.requiredKeywords.push(requiredRics);
                let requiredRbdms = new OnBaseKeywordQuery(this.rbdmsNumberKeyword.id, this.rbdmsNumberKeyword.name, this.organization.historicalOwnerNumber, "eq");
                requiredRbdms.relation = KeywordRelation.And;
                if (this.organization.historicalOwnerNumber && this.organization.historicalOwnerNumber.length > 0) {
                    this.requiredKeywords.push(requiredRbdms);
                }
            })
        ).subscribe(
            () => { },
            (error) => { this.handleError(error); }
        );
        this.subscription.add(word$);
        if (this.organization != null) {
            this.ricsOption.text = `${this.ricsOption.text} (${this.organization.id})`;
            this.identifierOptions.push(this.ricsOption);
            if (this.organization.historicalOwnerNumber != null && this.organization.historicalOwnerNumber != "") {
                this.identifier = "both";
                this.rbdmsOption.text = `${this.rbdmsOption.text} (${this.organization.historicalOwnerNumber})`;
                this.identifierOptions.push(this.rbdmsOption);
                this.identifierOptions.push(this.bothOption);
            } else {
                this.identifier = "rics";
            }
        }
        
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    getRequiredKeywords(): Observable<OnBaseKeyword[]> {
        let obs$: Observable<OnBaseKeyword[]>;
        if (this.rbdmsNumberKeyword != null && this.ricsNumberKeyword != null) {
            obs$ = of([this.rbdmsNumberKeyword, this.ricsNumberKeyword]);
        } else {
            obs$ = this._docService.getAllKeywords().pipe(
                map((allKeywords: OnBaseKeyword[]) => {
                    this.ricsNumberKeyword = allKeywords.find(k => k.name.toLocaleLowerCase().includes("rics owner id number"));
                    this.rbdmsNumberKeyword = allKeywords.find(k => k.name.toLocaleLowerCase().includes("legacy rbdms owner id number"));
                    return [this.ricsNumberKeyword, this.rbdmsNumberKeyword];
                })
            );
        }
        return obs$;
    }

    handleError(error: any): void {
        console.error(error);
        this.toggleLoader();
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }

    onPageChanged(data: PageData): void {
        const newQuery: OnBaseQueryModel = {
            documentTypeIds: this.query.documentTypeIds,
            documentTypeNames: this.query.documentTypeNames,
            matchAll: this.query.matchAll,
            maxResults: data.resultsPerPage * 5,
            resultsPerPage: data.resultsPerPage,
            fromDate: this.query.fromDate,
            toDate: this.query.toDate,
            sort: this.query.sort,
            isAscending: this.query.isAscending,
            searchParameters: this.query.searchParameters
        } as OnBaseQueryModel;
        this._loadingMessage = `Retrieving page ${data.pageNumber} at ${data.resultsPerPage} results per page.. please wait`;
        this.runQuery(newQuery);
    }

    runQuery(model: OnBaseQueryModel): void {
        if (model != null) {
            let rbdmsKeyword = new OnBaseKeywordQuery(this.rbdmsNumberKeyword.id, this.rbdmsNumberKeyword.name, this.organization.historicalOwnerNumber, "eq");
            rbdmsKeyword.relation = KeywordRelation.And;
            let ricsKeyword = new OnBaseKeywordQuery(this.ricsNumberKeyword.id, this.ricsNumberKeyword.name, this.organization.id, "eq");
            ricsKeyword.relation = KeywordRelation.And;
            if (this.identifier === this.ricsOption.id) {
                const ricsIndex = model.searchParameters.findIndex(p => p.keywordTypeName.toLocaleLowerCase() === this.ricsNumberKeyword.name.toLocaleLowerCase());
                if (ricsIndex === -1) {
                    model.searchParameters.push(ricsKeyword);
                }
                const rbdmsIndex = model.searchParameters.findIndex(p => p.keywordTypeName.toLocaleLowerCase() === this.rbdmsNumberKeyword.name.toLocaleLowerCase());
                if (rbdmsIndex !== -1) {
                    model.searchParameters.splice(rbdmsIndex, 1);
                }
            } else if (this.identifier === this.rbdmsOption.id) {
                const rbdmsIndex = model.searchParameters.findIndex(p => p.keywordTypeName.toLocaleLowerCase() === this.rbdmsNumberKeyword.name.toLocaleLowerCase());
                if (rbdmsIndex === -1) {
                    model.searchParameters.push(rbdmsKeyword);
                }
                const ricsIndex = model.searchParameters.findIndex(p => p.keywordTypeName.toLocaleLowerCase() === this.ricsNumberKeyword.name.toLocaleLowerCase());
                if (ricsIndex !== -1) {
                    model.searchParameters.splice(ricsIndex, 1);
                }
            } else {
                const ricsIndex = model.searchParameters.findIndex(p => p.keywordTypeName.toLocaleLowerCase() === this.ricsNumberKeyword.name.toLocaleLowerCase());
                if (ricsIndex === -1) {
                    model.searchParameters.push(ricsKeyword);
                }
                const rbdmsIndex = model.searchParameters.findIndex(p => p.keywordTypeName.toLocaleLowerCase() === this.rbdmsNumberKeyword.name.toLocaleLowerCase());
                if (rbdmsIndex === -1) {
                    model.searchParameters.push(rbdmsKeyword);
                }
            }
            this.toggleLoader();
            this.query = model;
            if (this.loadingMessage == null || this.loadingMessage.length === 0) {
                this._loadingMessage = this._defaultLoadingMessage;
            }
            const data$ = this._docService.query(model).subscribe(
                (data: QueryResult) => {
                    this.documents = data;
                    this.toggleLoader();
                    this._loadingMessage = "";
                },
                (error) => { this.handleError(error); }
            );           
            this.subscription.add(data$);
        }
    }

    onSorting(data: SortArgs): void {
        let sort: SortAttribute = SortAttribute.DocumentID;
        switch (data.sortProperty.toLocaleLowerCase()) {
            case "document name":
                sort = SortAttribute.DocumentName;
                break;
            case "document type":
                sort = SortAttribute.DocumentTypeName;
                break;
            default:
                sort = SortAttribute.DocumentID;
                break;
        }
        const newQuery: OnBaseQueryModel = {
            documentTypeIds: this.query.documentTypeIds,
            documentTypeNames: this.query.documentTypeNames,
            matchAll: this.query.matchAll,
            maxResults: this.query.maxResults,
            resultsPerPage: this.query.resultsPerPage,
            displayKeywordTypes: this.query.displayKeywordTypes,
            fromDate: this.query.fromDate,
            toDate: this.query.toDate,
            pageNumber: 1,
            searchParameters: this.query.searchParameters,
            isAscending: data.sortDirection === SortDirection.Ascending,
            sort: sort
        } as OnBaseQueryModel;
        this._loadingMessage = "Resorting.. please wait";
        this.runQuery(newQuery);
    }

    toggleLoader(): void {
        this.showLoader = !this.showLoader;
    }
}
